import { z } from 'zod';
/**
 * Display units for speed, distance and volume.
 *
 * **Telemetry is stored and transmitted in ONE set of units — km/h, km, litres
 * — always.** These preferences change how a number is rendered and nothing
 * else. A field named `kmPerL` means kilometres per litre in every response to
 * every tenant, forever; converting in the API would make the meaning of a
 * response depend on who asked for it, and a stored figure ambiguous the moment
 * a setting changed.
 *
 * That is also why conversion lives behind one set of formatters in the web
 * app rather than being applied ad hoc: a number converted twice, or converted
 * on a page that forgot to, is a class of bug with no visible symptom.
 *
 * ## Gallons are two different volumes
 *
 * An imperial gallon is 4.546 L and a US gallon is 3.785 L — a 20% difference.
 * "mpg" alone therefore does not identify a unit, and a UK fleet shown US mpg
 * would read figures a fifth too low without anything looking wrong. Both are
 * offered explicitly and neither is called just "gallons".
 */
export const speedUnitSchema = z.enum(['KPH', 'MPH']);
export const distanceUnitSchema = z.enum(['KM', 'MI']);
export const volumeUnitSchema = z.enum(['L', 'GAL_UK', 'GAL_US']);
/**
 * How dates and numbers READ — a BCP-47 locale, not a date-format enum.
 *
 * ⚠️ **A locale rather than `DD_MM_YYYY`, deliberately.** Asked for as a choice between
 * dd/mm/yyyy and mm/dd/yyyy; a locale gives that (en-GB is the first, en-US the second) and
 * carries the 12- or 24-hour clock, the decimal separator and the thousands separator with
 * it. A bare date-format enum would need each of those added beside it later, and four
 * settings that must agree about "which country is this reader in" is how one of them ends
 * up saying something different from the others.
 *
 * ⚠️ **A CLOSED list, not free text.** It is passed to `toLocaleString`, which silently
 * falls back for anything it does not recognise — so a typo would be stored, shown as
 * configured, and change nothing. A `z.enum` refuses it at the edge instead.
 *
 * ⚠️ **It does NOT change what a date input looks like.** Those are native
 * `<input type="date">` controls whose format comes from the BROWSER's own locale and
 * cannot be set by a server, by CSS or by JavaScript. Making them follow this means
 * replacing all eight with a custom picker, which is a separate decision with a
 * dependency attached.
 */
export const DISPLAY_LOCALES = ['en-GB', 'en-US', 'en-IE', 'en-AU', 'en-NZ', 'en-IN'];
export const displayLocaleSchema = z.enum(DISPLAY_LOCALES);
/**
 * Wording, with the date order spelled out — because that is what somebody is choosing.
 *
 * `Record<DisplayLocale, …>` so a locale added without wording is a COMPILE error, the
 * guarantee every closed vocabulary here gets.
 */
export const DISPLAY_LOCALE_LABELS = {
    'en-GB': 'United Kingdom — 31/12/2026, 24-hour',
    'en-US': 'United States — 12/31/2026, 12-hour',
    'en-IE': 'Ireland — 31/12/2026, 24-hour',
    'en-AU': 'Australia — 31/12/2026, 12-hour',
    'en-NZ': 'New Zealand — 31/12/2026, 12-hour',
    'en-IN': 'India — 31/12/2026, 12-hour',
};
export const unitPreferencesSchema = z.object({
    speed: speedUnitSchema,
    distance: distanceUnitSchema,
    volume: volumeUnitSchema,
    /*
     * ⚠️ On the UNITS chain rather than a second one, and the screen is already called
     * Regional defaults. Both answer "how do figures read to this customer", both inherit
     * partner → company → fleet account per field, and both are a fallback chain rather than
     * a narrowing. A second chain with its own resolver and its own screen is how two
     * settings about the same question come to disagree.
     */
    locale: displayLocaleSchema,
});
/**
 * What a tenant gets before anyone chooses.
 *
 * Metric, because that is what the CAN bus reports and what the platform
 * stores: the default should be the one that involves no conversion at all, so
 * a tenant that never visits settings sees the rawest possible figure.
 */
export const DEFAULT_UNIT_PREFERENCES = {
    speed: 'KPH',
    distance: 'KM',
    volume: 'L',
    /*
     * ⚠️ en-GB rather than the browser's own, because a DEFAULT has to be a decision
     * somebody can read off this file. Taking the viewer's browser would make the same
     * tenant's screens differ between two people looking at one incident, and a screenshot
     * in a support ticket would not match what the reporter saw.
     */
    locale: 'en-GB',
};
/**
 * Where a resolved unit came from.
 *
 * ⚠️ Reported per FIELD, and the screen must show it. A company administrator
 * who opens Units and sees miles has to know whether they chose that or their
 * partner did — otherwise an inherited value looks like a decision nobody made,
 * and the same reader concludes either that they cannot change it or that they
 * already have. This is the branding lesson: showing a tenant's empty column
 * made a company conclude its partner had set no logo.
 */
export const unitSourceSchema = z.enum(['TENANT', 'ORGANISATION', 'PARTNER', 'PLATFORM']);
export const unitSourcesSchema = z.object({
    speed: unitSourceSchema,
    distance: unitSourceSchema,
    volume: unitSourceSchema,
    locale: unitSourceSchema,
});
/** Accepts a partial update; anything omitted keeps its current value. */
export const unitPreferencesResponseSchema = z.object({
    tenantId: z.string().uuid(),
    units: unitPreferencesSchema,
    /**
     * Which level supplied each unit. `TENANT` is this tenant's own row;
     * `ORGANISATION` its parent's; `PARTNER` the white-label default; `PLATFORM`
     * the metric fallback nobody chose.
     */
    sources: unitSourcesSchema,
    /** The partner's name, when one supplied anything. For "set by Morgan Fuels". */
    partnerName: z.string().nullable(),
});
export const updateUnitPreferencesSchema = unitPreferencesSchema.partial().strict();
/**
 * A partner's defaults for its whole hierarchy.
 *
 * ⚠️ Every field is NULLABLE as well as optional, and the two mean different
 * things: **omitted leaves the value alone; explicit `null` CLEARS it**, so the
 * level below resumes inheriting. Collapsing them would make it impossible to
 * un-set a default without a migration — the same distinction the AI allowance
 * makes, where an omitted field is unchanged and an explicit null deletes the
 * row.
 */
export const updatePartnerUnitDefaultsSchema = z
    .object({
    speed: speedUnitSchema.nullable().optional(),
    distance: distanceUnitSchema.nullable().optional(),
    volume: volumeUnitSchema.nullable().optional(),
    locale: displayLocaleSchema.nullable().optional(),
})
    .strict();
/** A partner's defaults as stored. Null is "not set", never metric. */
export const partnerUnitDefaultsSchema = z.object({
    speed: speedUnitSchema.nullable(),
    distance: distanceUnitSchema.nullable(),
    volume: volumeUnitSchema.nullable(),
    locale: displayLocaleSchema.nullable(),
});
/**
 * `TenantSetting` keys these live under.
 *
 * Namespaced `units.*` so they sit alongside the existing `llm.*` group rather
 * than colliding with a future setting called `speed`.
 */
export const UNIT_SETTING_KEYS = {
    speed: 'units.speed',
    distance: 'units.distance',
    volume: 'units.volume',
    /*
     * ⚠️ Under `units.` so `readLevel`'s single prefix scan picks it up. A `display.` prefix
     * would read correctly and cost a second query per level for one value — and `units.*` is
     * already declared NOT parent-owned, which is exactly right here: this is a fallback
     * chain a branch may override, not a narrowing.
     */
    locale: 'units.locale',
};
// ── conversion ──────────────────────────────────────────────────────────────
//
// Exact by definition, not rounded approximations: a mile is exactly 1609.344 m
// and an imperial gallon exactly 4.54609 L. Rounding these would accumulate
// visible error in a fleet-wide total.
export const KM_PER_MILE = 1.609_344;
export const LITRES_PER_GAL_UK = 4.546_09;
export const LITRES_PER_GAL_US = 3.785_411_784;
export const SPEED_UNIT_LABELS = { KPH: 'km/h', MPH: 'mph' };
export const DISTANCE_UNIT_LABELS = { KM: 'km', MI: 'mi' };
export const VOLUME_UNIT_LABELS = {
    L: 'L',
    GAL_UK: 'gal (UK)',
    GAL_US: 'gal (US)',
};
/** Litres in one unit of the chosen volume. */
export function litresPer(unit) {
    return unit === 'GAL_UK' ? LITRES_PER_GAL_UK : unit === 'GAL_US' ? LITRES_PER_GAL_US : 1;
}
/** Kilometres in one unit of the chosen distance. */
export function kmPer(unit) {
    return unit === 'MI' ? KM_PER_MILE : 1;
}
/**
 * The label for a fuel-economy figure, derived rather than separately
 * configured.
 *
 * "miles per US gallon" is a distance choice and a volume choice, not a third
 * setting — offering economy independently would let a tenant pick km with
 * gallons and produce "km per gallon", which nobody uses and which would then
 * have to be defended.
 */
export function economyLabel(distance, volume) {
    if (distance === 'MI' && volume === 'GAL_UK')
        return 'mpg (UK)';
    if (distance === 'MI' && volume === 'GAL_US')
        return 'mpg (US)';
    return `${DISTANCE_UNIT_LABELS[distance]}/${VOLUME_UNIT_LABELS[volume]}`;
}
//# sourceMappingURL=units.js.map